/**
 * Single source of truth for rental slot metadata on the widget side. Mirrors
 * admin/src/helpers/slotTemplate.js but stays free of MUI/i18next so the widget
 * remains lightweight. A show's slot template lives in `marketing.rentalSlots`.
 */

export type SlotTemplateEntry = {
    key: string;
    label: string;
    labelI18n: Record<string, unknown> | null;
    timeOfDay: string;
    durationMinutes: number | null;
    isFullDay: boolean;
};

/** Localizer with the shape of useLanguage().getI18n. */
type GetI18n = (
    english: string | undefined,
    languageObj: { [key: string]: unknown } | undefined,
) => unknown;

/** Normalized, ordered slot template for a show. Empty when non-rental. */
export function getSlotTemplate(show: any): SlotTemplateEntry[] {
    const slots = show?.marketing?.rentalSlots;
    if (!Array.isArray(slots)) return [];
    return slots
        .filter((s: any) => s && s.key)
        .map((s: any) => ({
            key: s.key as string,
            label: (s.label as string) || '',
            labelI18n: (s.labelI18n as Record<string, unknown>) || null,
            timeOfDay: (s.timeOfDay as string) || '',
            durationMinutes: s.durationMinutes ?? null,
            isFullDay: !!s.isFullDay,
        }));
}

/** Sort index for a slot key (template order); Infinity when not present. */
export function slotOrderIndex(
    template: SlotTemplateEntry[],
    slotKey: string | null | undefined,
): number {
    const idx = (template || []).findIndex((s) => s.key === slotKey);
    return idx === -1 ? Infinity : idx;
}

/**
 * Display label for a timeslot (or template slot). Resolution order: localized
 * template label -> template label -> the slot key. Pass getI18n from
 * useLanguage to localize author-defined labels.
 */
export function resolveSlotLabel(
    template: SlotTemplateEntry[],
    timeslotOrSlot: { slotKey?: string | null; key?: string | null } | null | undefined,
    getI18n?: GetI18n,
): string {
    const slotKey = timeslotOrSlot?.slotKey ?? timeslotOrSlot?.key ?? null;
    const entry = slotKey ? (template || []).find((s) => s.key === slotKey) : null;
    if (entry) {
        if (getI18n && entry.labelI18n) {
            const localized = getI18n(entry.label, entry.labelI18n);
            if (typeof localized === 'string' && localized.length > 0) return localized;
        }
        if (entry.label) return entry.label;
    }
    return slotKey || '';
}
